// chat/Chat.tsx — the chat panel UI for Rightsize (cloned from Oriora Lockbox's src/chat/Chat.tsx).
// Purely ADDITIVE: it drives the SAME capabilities the app already has (log / fix / remove / search /
// recap / ask) by typing, scoped to whichever child is currently active on the main screen. All
// styling is namespaced under .otg-* so it can't collide with app CSS.
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm tap
// (a chat message can't destroy data on its own).

import { useState, useRef, useEffect } from "react";
import { askBrain, findInventoryByName, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type ResultRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.outgrown.chat.v1"; // device-only; lost on clear/reinstall

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
const destructive = (k: ChatAction["kind"]) => k === "delete_size" || k === "backup_restore" || k === "import_file" ||
        k === "library_remove" || k === "library_merge";
const warnFor = (action: ChatAction, childName: string | null): string =>
  
  action.kind === "library_merge"
    ? `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : action.kind === "library_remove"
      ? `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
      : `Remove ${action.kind === "delete_size" ? (action.label ?? "this size") : "this"} from ${childName ?? "the child"}'s history? This can't be undone — tap Confirm, or Cancel to keep it.`;
const confirmLabelFor = (action: ChatAction): string =>
  action.kind === "library_merge" ? "Confirm merge"
  : action.kind === "import_file" ? "Confirm import"
  : action.kind === "backup_restore" ? "Confirm restore"
  : "Confirm remove"; // library_remove / delete_size — genuinely a removal
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
type PendingHold = { action: ChatAction; rest: ChatAction[] };

// childId/childName are EMPTY when no child has been added yet. The chat still opens: the
// customer's library (save/list/move/merge/remove) belongs to THEM, not to a selected child, so
// gating the whole panel on "add a child first" made the library unreachable on a fresh install —
// the same bug Garage shipped with its no-vehicle guard (2026-08-13). Size actions still ask for a
// child; the brain returns that message per-action, below the library handling.
export default function Chat({ childId, childName, onChanged }: { childId: string; childName: string; onChanged?: () => void }) {
  const hasChild = !!childId;
  const greeting: ChatTurn = {
    role: "assistant",
    content: hasChild
      ? `Tell me what to do for ${childName} — I'll log it, fix it, find it, or recap the sizes.\n\n` +
        `• "log shoes size 13 brand Clarks" · "recap" · "what size is ${childName} in now?"`
      : `Add a child and I'll log and check their sizes. I can also keep notes and plans for you in the meantime.\n\n` +
        `• "save a note called Autumn list with content coat, boots" · "what's in my library?"`,
  };

  const [turns, setTurns] = useState<ChatTurn[]>(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
    } catch { /* ignore — start fresh */ }
    return [greeting];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a bought-ahead item. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
  }, [turns]);

  const transcriptOf = (t: ChatTurn[]) => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  async function runQueue(queue: ChatAction[], transcript: string): Promise<{ lines: string[]; rows?: ResultRow[]; changed: boolean; hold: PendingHold | null }> {
    const lines: string[] = [];
    let rows: ResultRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action.kind)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, { childId, childName, transcript });
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  // A photo turn is filed ON-DEVICE onto a bought-ahead inventory item (its garment photo): no model
  // call, no cost. The user names the item in the same message; fuzzy name-resolution finds the closest.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "(photo)" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const q = text.trim();
      if (!q) {
        setTurns([...next, { role: "assistant", content: "Which bought-ahead item is this photo for? Name it, then send." }]);
        return; // keep the photo attached so the next message can name it
      }
      const { matches, notFound } = await findInventoryByName(childId, q);
      if (matches.length === 0) {
        setTurns([...next, { role: "assistant", content: `${notFound} Add it in the inventory first, then I'll attach the photo.` }]);
        return; // keep the photo attached
      }
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((i) => `• ${i.description || i.category}${i.size_label ? ` (${i.size_label})` : ""}`).join("\n");
        setTurns([...next, { role: "assistant", content: `More than one item matches — which one?\n${list}` }]);
        return; // keep the photo attached
      }
      const target = matches[0];
      await store.updateInventoryItem(target.id, { photo_ref: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `Added your photo to "${target.description || target.category}".` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, { childId, childName }, onChunk);
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, transcriptOf(next));
      if (r.changed) onChanged?.();
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action, childName) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", rows: r.rows }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      let rows: ResultRow[] | undefined;
      const result = await executeAction(action, { childId, childName, transcript: transcriptOf(turns) });
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      const r = await runQueue(rest, transcriptOf(turns));
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action, childName) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([greeting]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : " Tell me what else to do.") }]);
  }

  return (
    <div className="otg-chat">
      <div className="otg-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`otg-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`otg-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <Rows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="otg-msgrow"><div className="otg-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="otg-composer otg-confirm">
          <button className="btn btn-danger" onClick={confirmPending} disabled={busy}>{confirmLabelFor(pending.action)}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="otg-composer">
            <textarea
              value={input}
              placeholder={photo ? "Name the bought-ahead item…" : `Tell me what to do for ${childName}…`}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. The gate here used to be iOS-only because no other path existed. */}
            {<button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}

function Rows({ rows }: { rows: ResultRow[] }) {
  return (
    <div className="otg-rows">
      {rows.map((r) => (
        <div key={r.id} className="otg-rowitem">
          <span className="otg-rowmain">
            {r.primary}
            {r.secondary && <span className="muted"> — {r.secondary}</span>}
          </span>
          {r.trailing && <span className="tnum" style={{ fontWeight: 600 }}>{r.trailing}</span>}
        </div>
      ))}
    </div>
  );
}
